import { decode, JwtPayload } from 'jsonwebtoken';
import { customerRole, candidateRole, displayContents } from './constants';
import { getQueryParam, getUniqueQueryString, hasValue, safeParseInt } from '../lib/utils';

let revokeTokenFn: ((accessToken: string, refreshToken: string) => void) | null = null;

export const registerRevokeTokenHandler = (fn: (accessToken: string, refreshToken: string) => void) => {
	revokeTokenFn = fn;
};

export const sessionKeys = {
	refreshToken: 'refreshToken',
	accessToken: 'accessToken',
	selectedSubCustomerId: 'selectedCustomerId',
	refereeToken: 'refereeToken',
	workflowId: 'workflowId', // Used only for Referees
	setPasswordSecret: 'secret',
	setPasswordEmail: 'email',
	companyName: 'companyName',
	invitationToken: 'token',
	workflowGroupId: 'workflowGroupId',
	apiVersionNumber: 'apiVersionNumber',
	brandName: 'brand',
	header: 'header',
	ConnectIdNonce: 'nonce',
	ConnectIdState: 'state',
	ConnectIdCodeVerifier: 'codeVerifier',
	ConnectIdAuthorisationServerId: 'authorisationServerId',
	redirectAppUrl: 'redirectAppUrl'
};

export const isLoggedIn = (): boolean => localStorage.getItem(sessionKeys.accessToken) !== null;

// Warning: code called directly after this function may or may not still execute:
// https://stackoverflow.com/questions/10525584/what-happens-to-code-after-a-javascript-redirect-setting-window-location-href
export const logOut = (sessionExpired = false) => {
	const refreshToken = getRefreshToken();
	const accessToken = getAccessToken();
	if (refreshToken && accessToken) {
		revokeTokenFn?.(accessToken, refreshToken);
	}
	clearApplicationStorage();
	const redirectUrl = getRedirectUrl();
	const logoutUrl = `/${sessionExpired ? `?sessionExpired=true` : ``}`;
	window.location.href = logoutUrl.includes('?') ? `${logoutUrl}&${redirectUrl}` : `/?${redirectUrl}`;
};

const getRedirectUrl = () => {
	if (window.location.href.includes('redirectAppUrl')) return `redirectAppUrl=${getQueryParam('redirectAppUrl')}`;
	if (localStorage.getItem('redirectAppUrl') != null) return `redirectAppUrl=${localStorage.getItem('redirectAppUrl')}`;
	return `redirectAppUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`;
};

export const conditionallyUpdateCacheAndGetSelectedSubCustomerId = () => {
	const subCustomerIdFromStorage = getSelectedSubCustomerIdFromStorage();
	if (subCustomerIdFromStorage) return subCustomerIdFromStorage;

	const defaultedSubCustomerId = getCustomerIdFromAccessToken();
	saveSelectedSubCustomerIdToStorage(defaultedSubCustomerId);
	return defaultedSubCustomerId;
};

export const getEmailFromAccessToken = () => {
	const token = getAccessToken();
	const decoded = decode(token) as CommonJwtPayload;
	return decoded.sub;
};

export const getCustomerIdFromAccessToken = (): number => {
	const token = getAccessToken();
	const decoded = decode(token) as CandidateJwtPayload | CustomerJwtPayload;
	const id = hasCustomerRole()
		? (decoded as CustomerJwtPayload)?.CustomerID
		: (decoded as CandidateJwtPayload)?.SubCustomerId;
	if (id === null) logOut();
	if (Number.isNaN(id)) throw new Error(`customerId wasn't a number: ${id}`);
	return safeParseInt(id);
};

export const getClientCustomerIdFromAccessToken = (): number => {
	const token = getAccessToken();
	const decoded = decode(token) as CandidateJwtPayload;
	return safeParseInt(decoded.ClientCustomerId);
};

export const getClientCustomerCountryISOCodeFromAccessToken = (): string | null => {
	const token = getAccessToken();
	const decoded = decode(token) as CandidateJwtPayload;
	return decoded.ClientCustomerCountryISOCode || null;
};

export const getCandidateIdFromAccessToken = (): string => {
	const token = getAccessToken();
	const decoded = decode(token) as CandidateJwtPayload;
	return decoded.CandidateId;
};

export const getCustomerUserIdFromAccessToken = (): number => {
	const token = getAccessToken();
	const decoded = decode(token) as CustomerJwtPayload;
	return decoded.UserID;
};

interface CommonJwtPayload extends JwtPayload {
	sub: string;
}

export interface CustomerJwtPayload extends CommonJwtPayload {
	UserID: number;
	CustomerID: string;
	role: string;
}

interface CandidateJwtPayload extends CommonJwtPayload {
	CandidateId: string;
	ClientCustomerId: string;
	SubCustomerId: string;
	ClientCustomerCountryISOCode: string;
}

export const hasAdminRole = () => hasCustomerAdminRole() || hasSystemAdminRole() || hasCustomerSuccessRole();

export const hasCustomerSuccessRoleOrHigher = () => hasCustomerSuccessRole() || hasSystemAdminRole();

export const hasCustomerAdminRoleOrHigher = () => hasCustomerAdminRole() || hasSystemAdminRole();

export const hasStandardUserRole = () => hasCustomerRole() && getLoginRole() === 'Standard User';

export const hasCustomerAdminRole = () => hasCustomerRole() && getLoginRole() === 'Customer Admin';

export const hasSystemAdminRole = () => hasCustomerRole() && getLoginRole() === 'System Admin';

export const hasCustomerSuccessRole = () => hasCustomerRole() && getLoginRole() === 'Customer Success Admin';

export const hasCustomerRole = () => getRole() === customerRole;

export const hasCandidateRole = () => getRole() === candidateRole;

export const isRootCustomerUser = () => getCustomerId() === '14';

export const isCustomerLevelUser = () => (hasCustomerAdminRole() || hasCustomerSuccessRole()) && !isRootCustomerUser();

export const isReadOnlyUser = () => getLoginRole() === 'Read Only';

export const showHeaderAndMenu = () =>
	hasValue(localStorage.getItem(sessionKeys.refreshToken)) && !window.location.href.includes(displayContents);

const getCustomerId = () => {
	const accessToken = getDecodedAccessToken();
	return accessToken ? accessToken.CustomerID : null;
};

// Seem to have runtime null issues when this is at the bottom of the file
const getDecodedAccessToken = (): AccessToken => {
	const accessToken = getAccessToken();
	if (accessToken === null) {
		return null;
	}
	return decode(accessToken) as AccessToken;
};

export const getRole = () => {
	const accessToken = getDecodedAccessToken();
	return accessToken ? accessToken.role : null;
};

const getLoginRole = () => {
	const accessToken = getDecodedAccessToken();
	return accessToken ? accessToken.LoginRole : null;
};

export const getRefreshToken = (): string => localStorage.getItem(sessionKeys.refreshToken);

export const getAccessToken = (): string => localStorage.getItem(sessionKeys.accessToken);

export const getRefereeToken = (): string => localStorage.getItem(sessionKeys.refereeToken);

export const getWorkflowId = (): string => localStorage.getItem(sessionKeys.workflowId);

export const getSetPasswordQueryStringsFromStorage = () => {
	return [
		localStorage.getItem(sessionKeys.setPasswordSecret),
		localStorage.getItem(sessionKeys.setPasswordEmail),
		localStorage.getItem(sessionKeys.companyName)
	];
};

export const getSetPasswordQueryStringsFromQuery = (location: Location) => {
	return [
		getUniqueQueryString(location.search, sessionKeys.setPasswordSecret),
		getUniqueQueryString(location.search, sessionKeys.setPasswordEmail),
		getUniqueQueryString(location.search, sessionKeys.companyName)
	];
};

export const getInvitationQueryStringsFromStorage = () => {
	return [
		localStorage.getItem(sessionKeys.invitationToken),
		localStorage.getItem(sessionKeys.workflowGroupId),
		localStorage.getItem(sessionKeys.companyName)
	];
};

export const getInvitationQueryStringsFromQuery = (location: Location) => {
	return [
		getUniqueQueryString(location.search, sessionKeys.invitationToken),
		getUniqueQueryString(location.search, sessionKeys.workflowGroupId),
		getUniqueQueryString(location.search, sessionKeys.companyName)
	];
};

export const getRefereeTokenAndWorkflowIdFromQuery = (location: Location) => {
	return [
		getUniqueQueryString(location.search, sessionKeys.refereeToken),
		getUniqueQueryString(location.search, sessionKeys.workflowId)
	];
};

export const saveTokens = (refreshToken: string, accessToken: string): void => {
	localStorage.setItem(sessionKeys.refreshToken, refreshToken);
	localStorage.setItem(sessionKeys.accessToken, accessToken);
};

export const saveAccessToken = (accessToken: string): void =>
	localStorage.setItem(sessionKeys.accessToken, accessToken);

const getSelectedSubCustomerIdFromStorage = (): number => {
	const value = localStorage.getItem(sessionKeys.selectedSubCustomerId);
	return safeParseInt(value);
};

export const saveSelectedSubCustomerIdToStorage = (subCustomerId: number): void => {
	if (hasValue(subCustomerId)) {
		localStorage.setItem(sessionKeys.selectedSubCustomerId, subCustomerId.toString());
	}
};

export const clearApplicationStorage = () => {
	localStorage.removeItem(sessionKeys.refreshToken);
	localStorage.removeItem(sessionKeys.accessToken);
	localStorage.removeItem(sessionKeys.selectedSubCustomerId);
	clearRefereeTokenInLocalStorage();
};

export const setRefereeTokenAndWorkflowIdInLocalStorage = (refereeToken: string, workflowId: string) => {
	localStorage.setItem(sessionKeys.refereeToken, refereeToken);
	localStorage.setItem(sessionKeys.workflowId, workflowId);
};

export const clearRefereeTokenInLocalStorage = () => {
	localStorage.removeItem(sessionKeys.refereeToken);
};

export const setSetPasswordQueryStringsInLocalStorage = (secret: string, email: string, companyName: string) => {
	localStorage.setItem(sessionKeys.setPasswordSecret, secret);
	localStorage.setItem(sessionKeys.setPasswordEmail, email);
	localStorage.setItem(sessionKeys.companyName, companyName);
};

export const clearSetPasswordQueryStringsInLocalStorage = () => {
	localStorage.removeItem(sessionKeys.setPasswordSecret);
	localStorage.removeItem(sessionKeys.setPasswordEmail);
	localStorage.removeItem(sessionKeys.companyName);
};

export const setInvitationQueryStringsInLocalStorage = (
	invitationToken: string,
	workflowGroupId: string,
	companyName: string
) => {
	localStorage.setItem(sessionKeys.invitationToken, invitationToken);
	localStorage.setItem(sessionKeys.workflowGroupId, workflowGroupId);
	localStorage.setItem(sessionKeys.companyName, companyName);
};

export const clearInvitationQueryStringsInLocalStorage = () => {
	localStorage.removeItem(sessionKeys.invitationToken);
	localStorage.removeItem(sessionKeys.workflowGroupId);
	localStorage.removeItem(sessionKeys.companyName);
};

if (isLoggedIn()) {
	// This is extremely helpful for development and saves you retrieving the token and manually decoding it.
	// eslint-disable-next-line no-console
	console.log('Customer Id:', getCustomerIdFromAccessToken());
}

export const setBrandNameQueryStringsInLocalStorage = () => {
	const brandName = getQueryParam(sessionKeys.brandName);
	if (hasValue(brandName)) {
		localStorage.setItem(sessionKeys.brandName, brandName);
	}
};

export const getBrandNameQueryStringsInLocalStorage = () => {
	return localStorage.getItem(sessionKeys.brandName);
};

interface AccessToken {
	role: string; // TODO consider deleting
	LoginRole: string;
	CustomerID: string;
}
